import Icon from './Icon';
import { Segmented, cx } from './primitives';

/**
 * Player identity P1–P6. A player colour is never shown without its P-label.
 * Colours are tokens (--egdl-player-N) so they swap with the theme.
 */
export const playerColor = (slot) => `var(--egdl-player-${((Math.max(1, slot) - 1) % 6) + 1})`;

const STATES = {
    joined: { text: 'Joined', ink: 'var(--egdl-success-ink)', icon: 'check' },
    lost: { text: 'Lost — held', ink: 'var(--egdl-danger)', icon: 'unlink' },
    waiting: { text: 'Waiting', ink: 'var(--egdl-warning-ink)', pulse: true },
    empty: { text: 'Empty', ink: 'var(--egdl-text-secondary)', icon: 'dash' },
    ended: { text: 'Left', ink: 'var(--egdl-text-secondary)', icon: 'dash' },
};

function blockStyle(slot, state) {
    const c = playerColor(slot);
    const hollow = state === 'empty' || state === 'waiting';
    return {
        background: hollow ? 'transparent' : c,
        borderColor: hollow ? (state === 'empty' ? 'var(--egdl-border-strong)' : c) : c,
        borderStyle: state === 'empty' ? 'dashed' : 'solid',
        color: hollow ? (state === 'empty' ? 'var(--egdl-text-secondary)' : 'var(--egdl-text-primary)') : 'var(--egdl-player-ink)',
    };
}

function displayName(name, state) {
    if (state === 'empty') return 'Open slot';
    if (state === 'waiting') return 'Waiting to join';
    return name || 'Guest';
}

/** variant: tag (P-label only) · compact (tag + name) · full (roster card) */
export default function PlayerChip({ slot = 1, name, state = 'joined', guest, variant = 'compact', showGuestNote = true, tracker, movement, selected, onClick, title }) {
    const isGuest = !!guest || name === 'Guest';
    const dn = displayName(name, state);
    const st = isGuest && state === 'joined' ? { text: 'Guest · not recorded', ink: 'var(--egdl-text-secondary)', icon: 'eyeOff' } : STATES[state] || STATES.joined;
    const tip = title || `Player ${slot} · ${dn} · ${st.text}`;

    if (variant === 'tag') {
        return <span className="eg-ptag" title={tip} style={blockStyle(slot, state)}>P{slot}</span>;
    }
    if (variant === 'compact') {
        return (
            <span className="eg-pchip" title={tip}>
                <span className="eg-ptag" style={blockStyle(slot, state)}>P{slot}</span>
                <span className="eg-pchip__name">{dn}</span>
                {isGuest && showGuestNote && state === 'joined' ? <span className="eg-caps-14 eg-secondary eg-nowrap">Not recorded</span> : null}
            </span>
        );
    }
    const hollow = state === 'empty' || state === 'waiting';
    return (
        <button type="button" className="eg-pfull" aria-pressed={onClick ? !!selected : undefined} title={tip} onClick={onClick} disabled={!onClick}>
            <span className="eg-pfull__block" style={blockStyle(slot, state)}>P{slot}</span>
            <span className="eg-pfull__text">
                <span className="eg-pfull__name" style={{ color: hollow ? 'var(--egdl-text-secondary)' : undefined }}>{dn}</span>
                <span className="eg-status" style={{ color: st.ink, gap: 6 }}>
                    {st.pulse ? <span className="eg-dot eg-dot--pulse" style={{ background: 'var(--egdl-warning)' }} /> : null}
                    {st.icon ? <Icon name={st.icon} size={16} /> : null}
                    <span>{st.text}</span>
                </span>
                {tracker ? <span className="eg-pfull__line">{tracker}</span> : null}
                {movement ? <span className="eg-pfull__line" style={{ color: 'var(--egdl-text-tertiary)' }}>{movement}</span> : null}
            </span>
        </button>
    );
}

/** PlayerSwitch — segmented All / P1…Pn with colour squares; drives the focused player. */
export function PlayerSwitch({ slots, value, onChange, label = 'Player focus', className }) {
    const options = [{ value: 'all', label: 'All' }, ...slots.map((s) => ({ value: s, label: 'P' + s, swatch: playerColor(s) }))];
    return <Segmented size="lg" className={cx(className)} label={label} options={options} value={value} onChange={onChange} />;
}
